/**
 * useOrgSettings
 *
 * Fetches the current organisation's settings (including UoM config, drone
 * image pipeline config, and satellite monitoring config) from the backend
 * and stores them in the auth store so every page can read them without
 * additional API calls.
 *
 * Usage:
 *   const { uom, imagePipeline, satelliteMonitoring, loading } = useOrgSettings();
 *   // uom.currency_symbol, imagePipeline.default_mode, satelliteMonitoring.frequency …
 */
import { useEffect, useState } from 'react';
import {
  useAuthStore,
  DEFAULT_UOM, OrgUomSettings,
  DEFAULT_IMAGE_PIPELINE, OrgImagePipelineSettings,
  DEFAULT_SATELLITE_MONITORING, OrgSatelliteMonitoringSettings,
} from '../store/authStore';
import api from '../services/api';

export interface UseOrgSettingsResult {
  uom: OrgUomSettings;
  imagePipeline: OrgImagePipelineSettings;
  satelliteMonitoring: OrgSatelliteMonitoringSettings;
  loading: boolean;
  /** Re-fetch from the server (call after saving changes) */
  refresh: () => void;
}

export function useOrgSettings(): UseOrgSettingsResult {
  const {
    currentOrganizationId,
    orgUomSettings, setOrgUomSettings,
    orgImagePipeline, setOrgImagePipeline,
    orgSatelliteMonitoring, setOrgSatelliteMonitoring,
  } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  const fetch = async () => {
    if (!currentOrganizationId) return;
    setLoading(true);
    try {
      const res = await api.get(`/organizations/${currentOrganizationId}`);
      const org = res.data?.data ?? res.data;
      const serverUom: Partial<OrgUomSettings> = org?.settings?.uom ?? {};

      // Also respect base_currency for symbol / code fallback
      const currencyCode: string = org?.base_currency ?? 'USD';
      const CURRENCY_SYMBOLS: Record<string, string> = {
        USD: '$', EUR: '€', GBP: '£', INR: '₹', AUD: 'A$', CAD: 'C$',
        SGD: 'S$', AED: 'د.إ', BRL: 'R$', JPY: '¥', CNY: '¥',
      };
      const symbolFallback = CURRENCY_SYMBOLS[currencyCode] ?? currencyCode;

      setOrgUomSettings({
        ...DEFAULT_UOM,
        currency_code:   currencyCode,
        ...serverUom,
        currency_symbol: serverUom.currency_symbol ?? symbolFallback,
      });

      // Load image pipeline settings
      const serverPipeline: Partial<OrgImagePipelineSettings> = org?.settings?.image_pipeline ?? {};
      setOrgImagePipeline({ ...DEFAULT_IMAGE_PIPELINE, ...serverPipeline });

      // Load satellite monitoring settings
      const serverSatellite: Partial<OrgSatelliteMonitoringSettings> = org?.settings?.satellite_monitoring ?? {};
      setOrgSatelliteMonitoring({ ...DEFAULT_SATELLITE_MONITORING, ...serverSatellite });
    } catch {
      // Non-fatal — fall back to defaults already in store
    } finally {
      setLoading(false);
      setFetched(true);
    }
  };

  useEffect(() => {
    if (!fetched) fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganizationId]);

  return {
    uom:                orgUomSettings,
    imagePipeline:      orgImagePipeline,
    satelliteMonitoring: orgSatelliteMonitoring,
    loading,
    refresh: fetch,
  };
}
